import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Waves, LogOut, User, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Layout({ children }) {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const nav = [
    { to: "/", label: "Find surf" },
    { to: "/surf-spots", label: "Surf spots" },
    { to: "/businesses", label: "Surf schools" },
    { to: "/about", label: "About" },
    { to: "/safety", label: "Safety" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 border-b border-border bg-[#F9F6F0]/85 backdrop-blur-xl"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            <div className="w-9 h-9 rounded-xl bg-teal-800 text-white flex items-center justify-center">
              <Waves className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg tracking-tight text-teal-900">Surfmatics</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Find your best surf
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                className={`px-3 py-2 rounded-full text-sm font-medium hover:bg-sand-200 ${
                  location.pathname === n.to ? "text-teal-800" : "text-muted-foreground"
                }`}
              >
                {n.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <div className="flex items-center gap-1 ml-2">
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="px-3 py-2 rounded-full text-sm font-medium hover:bg-sand-200 text-teal-800"
                >
                  Dashboard
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className="px-3 py-2 rounded-full text-sm font-medium hover:bg-sand-200 text-teal-800 flex items-center gap-1"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Admin
                  </Link>
                )}
                <button
                  data-testid="logout-button"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  className="ml-2 px-3 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-teal-900 hover:bg-sand-200 flex items-center gap-1"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 ml-2">
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="px-4 py-2 rounded-full text-sm font-medium text-teal-800 hover:bg-sand-200"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="px-4 py-2 rounded-full text-sm font-medium bg-teal-800 text-white hover:bg-teal-900"
                >
                  Get started
                </Link>
              </div>
            )}
          </nav>

          <button
            className="md:hidden p-2"
            onClick={() => setOpen((s) => !s)}
            data-testid="mobile-nav-toggle"
            aria-label="Open menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-border bg-white" data-testid="mobile-nav">
            <div className="px-5 py-4 flex flex-col gap-2">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mnav-${n.to.replace(/\//g, "") || "home"}`}
                  className="py-2 text-sm text-teal-900"
                >
                  {n.label}
                </Link>
              ))}
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="mnav-dashboard" className="py-2 text-sm text-teal-900">Dashboard</Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setOpen(false)} data-testid="mnav-admin" className="py-2 text-sm text-teal-900">Admin</Link>
                  )}
                  <button
                    onClick={async () => {
                      await logout();
                      setOpen(false);
                      navigate("/");
                    }}
                    data-testid="mnav-logout"
                    className="py-2 text-sm text-left text-terracotta-700"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} data-testid="mnav-login" className="py-2 text-sm text-teal-800">Log in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} data-testid="mnav-register" className="py-2 text-sm text-teal-800 font-semibold">Get started</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-16 border-t border-border bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-teal-900 text-lg">Surfmatics</div>
            <p className="text-sm text-muted-foreground max-w-md mt-1">
              A planning aid, not a safety authority. Conditions change quickly — assess locally and
              follow lifeguard and official advice.
            </p>
          </div>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link to="/surf-spots" data-testid="footer-surf-spots" className="hover:text-teal-800">Surf spots</Link>
            <Link to="/safety" data-testid="footer-safety" className="hover:text-teal-800">Safety</Link>
            <Link to="/about" data-testid="footer-about" className="hover:text-teal-800">About</Link>
            <a href="mailto:hello@surfmatics.com" data-testid="footer-contact" className="hover:text-teal-800">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
